import type { ReactNode } from 'react';
import { Icon } from '../../primitives/icon/index.ts';
import { Text } from '../../primitives/Text.tsx';
import { VisuallyHidden } from '../../primitives/VisuallyHidden.tsx';
import styles from './ChoiceTile.module.css';

/**
 * Design system § 14 — Choice tile: an option the user picks from a small set.
 *
 * CONTROLLED ONLY. The tile renders the state it is given and holds none of
 * its own. That is not a stylistic preference: a tile that tracked its own
 * selection could show an outcome the server has not confirmed, which FR-055
 * forbids, and the practice loop's phase machine is the single authority on
 * what state an option is in.
 *
 * The tile is domain-free. It takes a `label` and a `keyHint`; it does not know
 * what a note is (research D-025).
 */

export type ChoiceTileState =
  | 'default'
  | 'selected'
  | 'resolved-correct'
  | 'resolved-miss'
  | 'unchosen'
  | 'disabled';

/** What each state means to a reader who cannot see it (§ 12). */
const STATE_ANNOUNCEMENT: Record<ChoiceTileState, string | null> = {
  default: null,
  selected: 'selected',
  'resolved-correct': 'correct',
  'resolved-miss': 'incorrect',
  unchosen: null,
  disabled: null,
};

interface ChoiceTileProps {
  /** The option itself — rendered in `token-lg`, because it IS the content. */
  label: ReactNode;
  /**
   * The key that selects this option. § 12 requires a bound key to be VISIBLY
   * RENDERED on its target, not merely bound, so this is displayed as well as
   * announced.
   */
  keyHint?: string;
  state?: ChoiceTileState;
  onSelect?: () => void;
}

export function ChoiceTile({ label, keyHint, state = 'default', onSelect }: ChoiceTileProps) {
  const resolved = state === 'resolved-correct' || state === 'resolved-miss';
  const inert = state === 'unchosen' || state === 'disabled' || resolved;
  const announcement = STATE_ANNOUNCEMENT[state];

  return (
    <button
      type="button"
      className={styles.tile}
      data-state={state}
      disabled={state === 'disabled'}
      aria-pressed={state === 'selected' || resolved}
      aria-keyshortcuts={keyHint}
      onClick={inert ? undefined : onSelect}
    >
      {keyHint ? (
        <span className={`${styles.keyHint} type-numeric-inline`} aria-hidden="true">
          {keyHint}
        </span>
      ) : null}

      <Text variant="token-lg">{label}</Text>

      {/*
        § 12 — colour never alone. The outcome is a solid disc carrying a check
        or a cross, so the state survives a greyscale render, and the same word
        reaches a screen reader.
      */}
      {resolved ? (
        <span className={styles.marker} aria-hidden="true">
          <Icon name={state === 'resolved-correct' ? 'check' : 'close'} size={20} />
        </span>
      ) : null}

      {announcement ? <VisuallyHidden>{announcement}</VisuallyHidden> : null}
    </button>
  );
}
